"use client";

import React, { useRef, useEffect } from "react";
import GraySteroide from "../GraySteroide/GraySteroide";
import StyledDiv from "../../../../StyledDiv/StyledDiv";
import { IStyledDivStyles } from "../../../../StyledDiv/StyledDiv";
import Star, {
  IStarProps,
} from "../../../../Decorations/PetitPrince/Star/Star";
import { lePetitPrinceImages } from "../../../../../Assets/images/le-petit-prince";
import useBreakpoint from "../../../../Grid/hooks/useBreakpoint";
import { lazyGsap } from "../../../../../lib/gsap/lazyGsap";

interface IDecorationBaseProps {
  styles?: IStyledDivStyles;
  /** Référence à l'élément trigger pour le scroll */
  triggerRef?: React.RefObject<HTMLElement>;
  /** Activer l'animation de parallax au scroll */
  enableScrollAnimation?: boolean;
}

// Vitesses de parallax pour chaque décoration (multipliées par la hauteur du viewport)
// Valeurs positives = retardent légèrement avant de suivre le scroll
const PARALLAX_SPEEDS = {
  stars: { desktop: 0.15, mobile: 0.1 },
  graySteroide: { desktop: 0.25, mobile: 0.2 },
  yellowGlobe: { desktop: 0.2, mobile: 0.15 },
  yellowGlobeLittle: { desktop: 0.18, mobile: 0.12 },
  saturnPlanet: { desktop: 0.22, mobile: 0.18 },
  standingPrince: { desktop: 0.3, mobile: 0.25 },
}

const starsList: IStarProps[] = [
  {
    variant: "B",
    styles: {
      position: "absolute",
      top: "-22%",
      left: "-19%",
    },
  },
  {
    variant: "C",
    styles: {
      position: "absolute",
      top: "4%",
      left: "-17%",
    },
  },
  {
    variant: "D",
    styles: {
      position: "absolute",
      top: "20%",
      left: "-30%",
    },
  },
  {
    variant: "E",
    styles: {
      position: "absolute",
      top: "37%",
      left: "-17%",
    },
  },
  {
    variant: "F",
    styles: {
      position: "absolute",
      top: "25%",
      left: "2%",
    },
  },
  {
    variant: "G",
    styles: {
      position: "absolute",
      top: "4%",
      left: "18%",
    },
  },
  {
    variant: "H",
    styles: {
      position: "absolute",
      top: "-13%",
      left: "33%",
    },
  },
];

const DecorationBase = ({
  styles,
  triggerRef,
  enableScrollAnimation = true,
}: IDecorationBaseProps) => {
  const { isMobile } = useBreakpoint();
  const containerRef = useRef<HTMLDivElement>(null);
  const starsRef = useRef<HTMLDivElement>(null);
  const graySteroideRef = useRef<HTMLDivElement>(null);
  const yellowGlobeRef = useRef<HTMLDivElement>(null);
  const yellowGlobeLittleRef = useRef<HTMLDivElement>(null);
  const saturnPlanetRef = useRef<HTMLDivElement>(null);
  const standingPrinceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !enableScrollAnimation) return;

    const trigger = triggerRef?.current || containerRef.current;
    if (!trigger) return;

    let cancelled = false;
    let cleanup: (() => void) | undefined;

    lazyGsap().then(({ gsap, ScrollTrigger }) => {
      if (cancelled) return;

      const getSpeed = (speeds: { desktop: number; mobile: number }) =>
        isMobile ? speeds.mobile : speeds.desktop;

      const decorations = [
        { ref: starsRef, speed: getSpeed(PARALLAX_SPEEDS.stars) },
        { ref: graySteroideRef, speed: getSpeed(PARALLAX_SPEEDS.graySteroide) },
        { ref: yellowGlobeRef, speed: getSpeed(PARALLAX_SPEEDS.yellowGlobe) },
        { ref: yellowGlobeLittleRef, speed: getSpeed(PARALLAX_SPEEDS.yellowGlobeLittle) },
        { ref: saturnPlanetRef, speed: getSpeed(PARALLAX_SPEEDS.saturnPlanet) },
        { ref: standingPrinceRef, speed: getSpeed(PARALLAX_SPEEDS.standingPrince) },
      ];

      const animations: any[] = [];

      decorations.forEach(({ ref }) => {
        if (ref.current) {
          gsap.set(ref.current, { clearProps: "y" });
        }
      });

      decorations.forEach(({ ref, speed }) => {
        if (!ref.current) return;

        const viewportHeight = window.innerHeight;
        const moveDistance = viewportHeight * speed;

        const tween = gsap.to(ref.current, {
          y: moveDistance,
          ease: "none",
          immediateRender: false,
          scrollTrigger: {
            trigger: trigger,
            start: "top bottom",
            end: "bottom top",
            scrub: 1.2,
            invalidateOnRefresh: true,
          },
        });

        animations.push(tween);
      });

      ScrollTrigger.refresh();

      cleanup = () => {
        animations.forEach((tween) => {
          tween.scrollTrigger?.kill();
          tween.kill();
        });
        decorations.forEach(({ ref }) => {
          if (ref.current) {
            gsap.set(ref.current, { clearProps: "y" });
          }
        });
      };
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [enableScrollAnimation, triggerRef, isMobile]);

  return (
    <StyledDiv ref={containerRef} styles={styles} className="decoration-base">
      <StyledDiv
        as="div"
        styles={{
          position: "relative",
        }}
        className="decoration-base__container"
      >
        {/* Stars decoration wrapper */}
        <StyledDiv
          ref={starsRef}
          styles={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            willChange: "transform",
          }}
        >
          {starsList.map((star, index) => {
            // Calculate animationDuration between 2.5 and 4 based on index
            const animationDuration =
              2.5 + (index / (starsList.length - 1)) * (4 - 2.5);
            // Calculate animationDelay with 0.1 difference between elements
            const animationDelay = index * 0.1;

            return (
              <Star
                key={index}
                {...star}
                animationDuration={animationDuration}
                animationDelay={animationDelay}
              />
            );
          })}
        </StyledDiv>

        {/* GraySteroide decoration */}
        <StyledDiv
          ref={graySteroideRef}
          styles={{
            position: "relative",
            willChange: "transform",
          }}
        >
          <GraySteroide />
        </StyledDiv>

        {/* Yellow globe big */}
        <StyledDiv
          ref={yellowGlobeRef}
          styles={{
            position: "absolute",
            top: "-16%",
            left: "0%",
            willChange: "transform",
          }}
        >
          <svg
            width="66"
            height="70"
            viewBox="0 0 66 70"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M64.1504 42.5666C66.5523 34.5096 65.1025 27.6004 63.1333 23.4853C61.1642 19.3701 57.7019 11.8979 46.4496 5.53018C35.1973 -0.837486 26.7581 -0.664214 20.7208 0.938533C14.6836 2.56294 6.74203 9.38544 3.27979 16.2513C-0.182458 23.1171 -0.680152 32.9285 0.748024 39.1446C2.1762 45.3606 4.8378 53.8292 9.10069 58.3342C13.3636 62.8176 22.8415 70.3765 31.9515 69.2935C42.0786 68.0806 47.748 64.5719 50.2581 62.471C59.1301 55.0637 60.6232 54.4789 64.172 42.545"
              fill="#FFD655"
            />
          </svg>
        </StyledDiv>

        {/* Yellow globe little */}
        <StyledDiv
          ref={yellowGlobeLittleRef}
          styles={{
            position: "absolute",
            top: "14%",
            left: "14%",
            willChange: "transform",
          }}
        >
          <img
            src={lePetitPrinceImages.globeYellowSingleLittle}
            width={27}
            height="auto"
            alt=""
          />
        </StyledDiv>

        {/* Saturn planet with ring */}
        <StyledDiv
          ref={saturnPlanetRef}
          styles={{
            position: "absolute",
            top: "-25%",
            left: "49%",
            willChange: "transform",
          }}
        >
          <svg
            width="36"
            height="34"
            viewBox="0 0 36 34"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8.46395 4.59019C8.46395 4.59019 15.3235 1.51465 16.1891 1.57963C17.0546 1.6446 23.3083 1.77456 25.3857 2.70588C27.463 3.63721 31.7908 7.38417 32.44 8.51043C33.0891 9.65834 34.4524 13.8818 33.8681 18.0403C33.2839 22.1988 32.3967 26.3572 28.0472 28.9996C23.6978 31.642 15.8428 31.4904 12.4022 30.0176C8.96163 28.5231 2.81614 26.184 2.31844 17.997C1.84238 9.9399 8.46395 4.56854 8.46395 4.56854"
              fill="#FFD655"
            />
            <path
              d="M7.0357 5.04512C7.0357 5.04512 12.6618 1.34147 16.4054 1.18986C20.149 1.05991 25.3207 2.59768 25.7967 2.31611C26.2944 2.05621 29.5836 -0.152982 31.7475 0.0419462C33.9114 0.236875 35.7507 2.42441 35.253 4.54696C34.7553 6.69118 33.6301 8.92203 33.2406 9.68009C32.8511 10.4381 28.48 18.3652 25.1908 21.744C20.5817 26.4873 14.1982 30.0176 14.1982 30.0176C14.1982 30.0176 15.41 30.4075 18.2014 30.6241C21.8368 30.9057 25.6236 29.8011 27.2033 29.0863C29.194 28.1767 32.2668 24.6896 32.7645 22.567C33.2622 20.4228 33.6517 17.6505 33.7383 16.8924C33.8032 16.1127 33.5002 14.38 33.9547 14.4234C34.4091 14.4667 34.2143 22.0689 31.8773 25.7725C29.5403 29.4545 24.9961 31.2955 23.4381 31.4255C22.724 31.4688 20.1706 32.7683 13.1163 30.754C13.1163 30.754 10.4114 31.4904 9.52417 31.7287C8.20419 32.0752 6.538 32.4001 5.10983 32.7467C4.09279 32.9849 1.43117 33.8512 0.522329 31.902C-0.408149 29.9527 0.154488 29.0647 0.78202 28.8048C1.43119 28.5449 0.197775 31.707 1.90726 31.8586C3.42199 31.9886 5.54261 31.3388 6.53801 31.1006C9.32944 30.4075 10.4979 29.8011 10.4979 29.8011C10.4979 29.8011 6.42981 28.155 5.04491 26.2057C3.18395 23.585 2.05871 22.1772 1.82068 18.6035C1.58265 15.0298 3.16229 10.4598 4.24425 10.3948C4.24425 10.3948 2.59969 14.6183 2.59969 18.6685C2.59969 21.4191 5.00163 24.9495 6.73275 26.5089C8.46388 28.0684 11.3851 28.9347 11.3851 28.9347C13.7438 28.6748 19.7162 23.8882 20.7115 23.3468C21.6853 22.8053 27.463 17.3906 29.8 13.8602C32.137 10.3298 32.2452 8.94369 32.2452 8.94369C32.2452 8.94369 30.5357 7.08104 31.2065 6.66952C31.8773 6.25801 32.8294 7.75246 32.8294 7.75246C32.8294 7.75246 34.9717 4.67692 34.4956 2.92256C34.0196 1.1682 31.4229 0.778343 29.9947 0.973272C28.5665 1.1682 26.7272 2.70597 26.7272 2.70597C28.3718 3.46403 29.9298 5.17507 28.9777 5.39166C28.0256 5.6299 28.1554 4.07047 25.1259 3.18246C22.0965 2.29446 18.5693 1.51474 15.2369 2.31611C15.2369 2.31611 9.91369 4.35204 8.96157 4.72023C7.98781 5.11009 7.05733 5.02346 7.05733 5.02346"
              fill="#575757"
            />
            <path
              d="M7.0357 5.04512C7.0357 5.04512 12.6619 1.34147 16.4054 1.18986C20.149 1.05991 25.3207 2.59768 25.7967 2.31611C26.2944 2.05621 29.5836 -0.152982 31.7475 0.0419462C33.9114 0.236875 35.7507 2.42441 35.253 4.54696C34.7553 6.69118 33.6301 8.92203 33.2406 9.68009C32.8511 10.4381 28.48 18.3652 25.1908 21.744C20.5817 26.4873 14.1982 30.0176 14.1982 30.0176C14.1982 30.0176 15.41 30.4075 18.2014 30.6241C21.8368 30.9057 25.6236 29.8011 27.2033 29.0863C29.194 28.1767 32.2668 24.6896 32.7645 22.567C33.2622 20.4228 33.6517 17.6505 33.7383 16.8924C33.8032 16.1127 33.5002 14.38 33.9547 14.4234C34.4091 14.4667 34.2143 22.0689 31.8773 25.7725C29.5403 29.4545 24.9961 31.2955 23.4381 31.4255C22.724 31.4688 20.1706 32.7683 13.1163 30.754C13.1163 30.754 10.4114 31.4904 9.52417 31.7287C8.20419 32.0752 6.538 32.4001 5.10983 32.7466C4.09279 32.9849 1.43117 33.8512 0.522329 31.902C-0.408149 29.9527 0.154488 29.0647 0.78202 28.8048C1.43119 28.5449 0.197775 31.707 1.90726 31.8586C3.42199 31.9886 5.54261 31.3388 6.53801 31.1006C9.32944 30.4075 10.4979 29.8011 10.4979 29.8011C10.4979 29.8011 6.42981 28.155 5.04491 26.2057C3.18395 23.585 2.05871 22.1772 1.82068 18.6035C1.58265 15.0298 3.16229 10.4598 4.24425 10.3948C4.24425 10.3948 2.59969 14.6183 2.59969 18.6685C2.59969 21.4191 5.00163 24.9495 6.73275 26.5089C8.46388 28.0684 11.3851 28.9347 11.3851 28.9347C13.7438 28.6748 19.7162 23.8882 20.7115 23.3468C21.6853 22.8053 27.463 17.3906 29.8 13.8602C32.137 10.3298 32.2452 8.94369 32.2452 8.94369C32.2452 8.94369 30.5357 7.08104 31.2065 6.66952C31.8773 6.258 32.8294 7.75246 32.8294 7.75246C32.8294 7.75246 34.9717 4.67692 34.4956 2.92256C34.0196 1.1682 31.4229 0.778343 29.9947 0.973272C28.5665 1.1682 26.7272 2.70597 26.7272 2.70597C28.3718 3.46403 29.9298 5.17507 28.9777 5.39166C28.0256 5.6299 28.1554 4.07047 25.1259 3.18246C22.0965 2.29446 18.5693 1.51474 15.2369 2.31611C15.2369 2.31611 9.91369 4.35204 8.96157 4.72023C7.98781 5.11009 7.05733 5.02346 7.05733 5.02346L7.0357 5.04512Z"
              stroke="#575757"
              strokeWidth="0.06"
              strokeLinejoin="round"
            />
          </svg>
        </StyledDiv>

        {/* Standing Prince */}
        <StyledDiv
          ref={standingPrinceRef}
          styles={{
            position: "absolute",
            top: "-13%",
            left: "45%",
            willChange: "transform",
          }}
        >
          <img src={lePetitPrinceImages.standingPrince} alt="" />
        </StyledDiv>
      </StyledDiv>
    </StyledDiv>
  );
};

export default DecorationBase;
